import {
  getListMediaQueryKey,
  useCreateCaptionAsset,
} from '@reelplicate/api-client/media';
import { getListMediaCollectionsQueryKey } from '@reelplicate/api-client/media-collections';
import {
  Button,
  Checkbox,
  Field,
  Modal,
  ModalClose,
  ModalContent,
  Select,
  SelectItem,
  Stack,
  TextArea,
  TextInput,
  toast,
} from '@reelplicate/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useId, useMemo, useState } from 'react';

import { collectionsHolding, sceneSaves, slideCaptions } from './library';
import { useMedia } from './media-context';
import type { CaptionSource } from './media-outlet';
import { ENTRIES_KEY } from './use-collection-entries';

const NONE = '__none__';
const MAX = 2200;

/**
 * "New caption": copy written by hand, kept in the library as a Caption -
 * e.g. a bank of on-slide texts for Carousel from slots. It can go straight
 * into a hand-picked collection.
 */
export function CaptionModal({
  workspaceId,
  open,
  collectionId,
  caption,
  title,
  source,
  onOpenChange,
}: {
  workspaceId: string;
  open: boolean;
  /** Preselected collection (from a collection's page). */
  collectionId?: string;
  /** Prefilled text, e.g. a carousel slide's. */
  caption?: string;
  title?: string;
  /** The slide the text comes from. */
  source?: CaptionSource;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      {open && (
        <CaptionForm
          workspaceId={workspaceId}
          collectionId={collectionId}
          caption={caption}
          title={title}
          source={source}
          onDone={() => onOpenChange(false)}
        />
      )}
    </Modal>
  );
}

function CaptionForm({
  workspaceId,
  collectionId: initialCollection,
  caption: initialCaption,
  title: initialTitle,
  source,
  onDone,
}: {
  workspaceId: string;
  collectionId?: string;
  caption?: string;
  title?: string;
  source?: CaptionSource;
  onDone: () => void;
}) {
  const formId = useId();
  const queryClient = useQueryClient();
  const { assets, collections } = useMedia();
  const create = useCreateCaptionAsset();
  const [caption, setCaption] = useState(initialCaption ?? '');
  const [title, setTitle] = useState(initialTitle ?? '');
  const [collectionId, setCollectionId] = useState(initialCollection ?? NONE);
  const [addAnother, setAddAnother] = useState(false);
  const handPicked = collections.filter((c) => !c.smart);
  const count = [...caption].length;
  // Saved from this slide or scene before: say where, so it isn't saved twice by accident.
  const savedBefore = useMemo(() => {
    const from = source && assets.find((a) => a.id === source.assetId);
    const captions =
      from &&
      (source.scene !== undefined
        ? sceneSaves(from, assets).get(source.scene)?.captions
        : slideCaptions(from, assets).get(source.slide));
    return captions && collectionsHolding(collections, captions);
    // Only as the dialog opened: not the caption it has just saved.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    const text = caption.trim();
    if (!text) return;
    try {
      await create.mutateAsync({
        id: workspaceId,
        data: {
          caption: text,
          // Untitled: the first words stand in.
          title: title.trim() || [...text.replace(/\s+/g, ' ')].slice(0, 60).join(''),
          ...(collectionId !== NONE ? { collectionId } : {}),
          ...(source
            ? {
                sourceAssetId: source.assetId,
                ...(source.scene !== undefined
                  ? { sourceScene: source.scene }
                  : { sourceSlide: source.slide }),
              }
            : {}),
        },
      });
    } catch {
      return; // Shown as a toast.
    }
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: getListMediaQueryKey(workspaceId) }),
      queryClient.invalidateQueries({
        queryKey: getListMediaCollectionsQueryKey(workspaceId),
      }),
      queryClient.invalidateQueries({ queryKey: [ENTRIES_KEY, workspaceId] }),
    ]);
    toast.success('Caption saved');
    if (addAnother) {
      setCaption('');
      setTitle('');
    } else onDone();
  };

  return (
    <ModalContent
      title={initialCaption ? 'Save as caption' : 'New caption'}
      description="Copy you write once and reuse - post captions, or the text on a slide."
      footerStart={
        // Another caption from here would claim this slide as its source.
        !source && (
          <Checkbox
            label="Add another after this"
            checked={addAnother}
            onCheckedChange={(on) => setAddAnother(on === true)}
          />
        )
      }
      footer={
        <>
          <ModalClose asChild>
            <Button variant="ghost">Cancel</Button>
          </ModalClose>
          <Button
            type="submit"
            form={formId}
            loading={create.isPending}
            disabled={!caption.trim() || count > MAX}
          >
            Save caption
          </Button>
        </>
      }
    >
      <Stack
        as="form"
        id={formId}
        gap={4}
        onSubmit={(event) => {
          event.preventDefault();
          void save();
        }}
      >
        <Field
          label="Caption"
          count={count}
          maxLength={MAX}
          helper="Emoji and line breaks are kept."
        >
          <TextArea
            value={caption}
            placeholder="I printed QR cards and handed them to every guest!"
            onChange={(event) => setCaption(event.target.value)}
          />
        </Field>
        <Field label="Name" optional helper="Defaults to the first words.">
          <TextInput
            value={title}
            maxLength={200}
            onChange={(e) => setTitle(e.target.value)}
          />
        </Field>
        <Field label="Add to collection" optional>
          <Select value={collectionId} onValueChange={setCollectionId}>
            <SelectItem value={NONE}>None</SelectItem>
            {handPicked.map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.name}
              </SelectItem>
            ))}
          </Select>
        </Field>
        {savedBefore && (
          <p className="text-body-sm text-text-secondary">
            Already saved from this {source?.scene !== undefined ? 'scene' : 'slide'}
            {savedBefore.length > 0
              ? ` to ${savedBefore.map((c) => `“${c.name}”`).join(', ')}`
              : ' (in no collection)'}
            .
          </p>
        )}
      </Stack>
    </ModalContent>
  );
}
